import type { TFunction } from 'i18next';
import {
  variantRespondentCount,
  type ProcessStepRecordingDisplay,
  type CapacityBinding
} from '@bliks-io/contracts';
import { ScoreBadge, createColumnHeader, type DataTableColumnDef } from '@bliks-io/ui';
import { CapacityCell } from '@/components/capacity-cell';
import { meanSurveyMetric, firstSurveyFrequency } from '@/lib/surveyMetrics';

/**
 * The per-step variant counts (ADR 0022), over everyone who answered the check
 * (D12) — read off the step's own Variant Check block, so an answer counts
 * whether or not the classic survey behind it was ever finished. Counts, not
 * names: this table is a scan surface, and the attributed view lives on the
 * recording dashboard.
 *
 * Its own builder because it rides `readProcessInsights` while the rest of the
 * analytics columns ride `readSensitive` (ADR 0021).
 */
function buildVariantsColumn(t: TFunction): DataTableColumnDef<ProcessStepRecordingDisplay> {
  return {
    id: 'variants',
    accessorFn: (row) => row.variantCheck?.different ?? 0,
    meta: { label: t('processes.variants') },
    header: createColumnHeader(t('processes.variants')),
    cell: ({ row }) => {
      const variantCheck = row.original.variantCheck;
      const total = variantCheck ? variantRespondentCount(variantCheck) : 0;
      if (!variantCheck || total === 0) {
        return <span className="text-sm font-medium">{t('common.notAvailable')}</span>;
      }
      return (
        <span className="text-sm font-medium">
          {`${variantCheck.different}/${total} ${t('processFlow.variantCheck.different')}`}
          {variantCheck.notPerformed > 0 && (
            <span className="text-xs text-gray-8 ml-1">
              {`· ${variantCheck.notPerformed}/${total} ${t('processFlow.variantCheck.notPerformed')}`}
            </span>
          )}
        </span>
      );
    }
  };
}

/**
 * The analytics columns of the process step table, in their canonical order —
 * this module owns that order, callers only say which capabilities the viewer
 * holds. Rating, frequency, duration and capacity aggregate each step's surveys
 * and ride `readSensitive` (`showManagerColumns`); variants rides
 * `readProcessInsights` (`showVariantCheck`) and can be the only one present.
 */
interface AnalyticsColumnParams {
  t: TFunction;
  showManagerColumns: boolean;
  showVariantCheck: boolean;
  stepCapacities?: Record<string, CapacityBinding>;
  /** Per-step response coverage (ADR 0023 decision 4), 0..1 by step id. */
  stepCoverage?: Record<string, number> | null;
  formatFrequency: (frequency?: string, timesPerPeriod?: number | null) => string;
  formatDuration: (durationInMinutes?: number) => string;
}

export function buildAnalyticsColumns({
  t,
  showManagerColumns,
  showVariantCheck,
  stepCapacities,
  stepCoverage,
  formatFrequency,
  formatDuration
}: AnalyticsColumnParams): DataTableColumnDef<ProcessStepRecordingDisplay>[] {
  const ratingColumn: DataTableColumnDef<ProcessStepRecordingDisplay> = {
    id: 'rating',
    accessorFn: (row) => meanSurveyMetric(row.surveys, (s) => s.rating?.stars) ?? undefined,
    meta: { label: t('processes.rating') },
    header: createColumnHeader(t('processes.rating')),
    cell: ({ row }) => (
      <ScoreBadge
        value={meanSurveyMetric(row.original.surveys, (s) => s.rating?.stars)}
        variant="boxed"
        colorScheme="dynamic"
      />
    )
  };

  const frequencyColumn: DataTableColumnDef<ProcessStepRecordingDisplay> = {
    id: 'frequency',
    // Same filter as the cell: the first survey row can now be an answerless
    // dismissal, which would sort and export the step as having no frequency.
    accessorFn: (row) => firstSurveyFrequency(row.surveys)?.value,
    meta: { label: t('processes.frequencyLabel') },
    header: createColumnHeader(t('processes.frequencyLabel')),
    cell: ({ row }) => {
      const withFrequency = (row.original.surveys || []).filter((s) => s.frequency?.value);
      const [first] = withFrequency;
      const additionalCount = withFrequency.length - 1;

      if (!first?.frequency) {
        return <span className="text-sm font-medium">{t('common.notAvailable')}</span>;
      }

      return (
        <span className="text-sm font-medium">
          {formatFrequency(first.frequency.value, first.frequency.timesPerPeriod)}
          {additionalCount > 0 && (
            <span className="text-xs text-gray-8 ml-1">
              {t('common.moreCount', { count: additionalCount })}
            </span>
          )}
        </span>
      );
    }
  };

  const durationColumn: DataTableColumnDef<ProcessStepRecordingDisplay> = {
    id: 'duration',
    accessorFn: (row) => {
      const surveys = row.surveys || [];
      const durations = surveys
        .map((s) => s.duration?.value)
        .filter((d): d is number => typeof d === 'number');
      return durations.length > 0
        ? durations.reduce((sum, d) => sum + d, 0) / durations.length
        : undefined;
    },
    meta: { label: t('processes.duration') },
    header: createColumnHeader(t('processes.duration')),
    cell: ({ row }) => {
      const surveys = row.original.surveys || [];
      const durations = surveys
        .map((s) => s.duration?.value)
        .filter((d): d is number => typeof d === 'number');
      const avgDuration =
        durations.length > 0
          ? durations.reduce((sum, d) => sum + d, 0) / durations.length
          : undefined;
      return <span className="text-sm font-medium">{formatDuration(avgDuration)}</span>;
    }
  };

  const capacityColumn: DataTableColumnDef<ProcessStepRecordingDisplay> = {
    id: 'capacity',
    accessorFn: (row) => stepCapacities?.[row._id]?.hoursPerYear ?? -1,
    meta: { label: t('processes.capacityBinding') },
    header: createColumnHeader(t('processes.capacityBinding')),
    cell: ({ row }) => {
      const capacity = stepCapacities?.[row.original._id];
      return (
        <CapacityCell
          capacity={capacity ?? null}
          // A manual-effective step carries the fixed source confidence, not
          // the survey's response coverage.
          confidence={capacity?.confidence ?? stepCoverage?.[row.original._id]}
        />
      );
    },
    enableSorting: true,
    sortFn: (rowA, rowB) => {
      const a = stepCapacities?.[rowA.original._id]?.hoursPerYear ?? -1;
      const b = stepCapacities?.[rowB.original._id]?.hoursPerYear ?? -1;
      return a - b;
    }
  };

  return [
    ...(showManagerColumns ? [ratingColumn, frequencyColumn, durationColumn] : []),
    ...(showVariantCheck ? [buildVariantsColumn(t)] : []),
    ...(showManagerColumns ? [capacityColumn] : [])
  ];
}
